import React, { useState } from 'react';
import { SearchIcon, SmartphoneIcon, AppleIcon, SettingsIcon, WifiIcon, QrCodeIcon, CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Button } from '../components/Button';
type Device = {
  id: string;
  brand: string;
  model: string;
  supportsESIM: boolean;
  releaseYear: number;
};
type GuideStep = {
  id: number;
  title: string;
  description: string;
  image: string;
  icon: React.ReactNode;
};
export function InstallationGuide() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('iphone');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(1);
  const toggleFaq = (id: number) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };
  const iphoneSteps: GuideStep[] = [{
    id: 1,
    title: 'Open Settings',
    description: 'Navigate to the Settings app on your iPhone',
    image: 'https://images.unsplash.com/photo-1584006682522-dc17d6c0d9ac?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
    icon: <SettingsIcon className="h-6 w-6" data-id="element-1068" />
  }, {
    id: 2,
    title: 'Tap Cellular/Mobile Data',
    description: 'Select Cellular or Mobile Data from the menu',
    image: 'https://images.unsplash.com/photo-1585060544812-6b45742d762f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
    icon: <SmartphoneIcon className="h-6 w-6" data-id="element-1069" />
  }, {
    id: 3,
    title: 'Add Cellular/Mobile Plan',
    description: 'Tap on "Add Cellular Plan" or "Add Mobile Plan"',
    image: 'https://images.unsplash.com/photo-1580910051074-3eb694886505?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
    icon: <QrCodeIcon className="h-6 w-6" data-id="element-1070" />
  }, {
    id: 4,
    title: 'Scan QR Code',
    description: 'Scan the QR code we provide after purchase',
    image: 'https://images.unsplash.com/photo-1613758235402-745466bb7ded?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
    icon: <QrCodeIcon className="h-6 w-6" data-id="element-1071" />
  }, {
    id: 5,
    title: 'Confirm and Activate',
    description: 'Confirm the details and activate your eSIM',
    image: 'https://images.unsplash.com/photo-1580910051074-3eb694886505?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
    icon: <CheckCircleIcon className="h-6 w-6" data-id="element-1072" />
  }];
  const androidSteps: GuideStep[] = [{
    id: 1,
    title: 'Open Settings',
    description: 'Navigate to the Settings app on your Android device',
    image: 'https://images.unsplash.com/photo-1598327105666-5b89351aff97?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
    icon: <SettingsIcon className="h-6 w-6" data-id="element-1073" />
  }, {
    id: 2,
    title: 'Network & Internet',
    description: 'Tap on "Network & Internet" or "Connections"',
    image: 'https://images.unsplash.com/photo-1592899677977-9c10ca588bbd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
    icon: <WifiIcon className="h-6 w-6" data-id="element-1074" />
  }, {
    id: 3,
    title: 'Mobile Network/SIMs',
    description: 'Select "Mobile Network", "SIMs" or "SIM Card Manager"',
    image: 'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
    icon: <SmartphoneIcon className="h-6 w-6" data-id="element-1075" />
  }, {
    id: 4,
    title: 'Add eSIM',
    description: 'Tap "Add eSIM", "Download a SIM" or "+" icon',
    image: 'https://images.unsplash.com/photo-1575718120842-54e388fc35f7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
    icon: <QrCodeIcon className="h-6 w-6" data-id="element-1076" />
  }, {
    id: 5,
    title: 'Scan QR Code',
    description: 'Scan the QR code we provide after purchase',
    image: 'https://images.unsplash.com/photo-1613758235402-745466bb7ded?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
    icon: <QrCodeIcon className="h-6 w-6" data-id="element-1077" />
  }];
  const compatibleDevices: Device[] = [{
    id: 'iphone-14-pro',
    brand: 'Apple',
    model: 'iPhone 14 Pro',
    supportsESIM: true,
    releaseYear: 2022
  }, {
    id: 'iphone-14',
    brand: 'Apple',
    model: 'iPhone 14',
    supportsESIM: true,
    releaseYear: 2022
  }, {
    id: 'iphone-13-pro',
    brand: 'Apple',
    model: 'iPhone 13 Pro',
    supportsESIM: true,
    releaseYear: 2021
  }, {
    id: 'iphone-13',
    brand: 'Apple',
    model: 'iPhone 13',
    supportsESIM: true,
    releaseYear: 2021
  }, {
    id: 'iphone-12-pro',
    brand: 'Apple',
    model: 'iPhone 12 Pro',
    supportsESIM: true,
    releaseYear: 2020
  }, {
    id: 'iphone-12',
    brand: 'Apple',
    model: 'iPhone 12',
    supportsESIM: true,
    releaseYear: 2020
  }, {
    id: 'iphone-11-pro',
    brand: 'Apple',
    model: 'iPhone 11 Pro',
    supportsESIM: true,
    releaseYear: 2019
  }, {
    id: 'iphone-11',
    brand: 'Apple',
    model: 'iPhone 11',
    supportsESIM: true,
    releaseYear: 2019
  }, {
    id: 'iphone-xs',
    brand: 'Apple',
    model: 'iPhone XS',
    supportsESIM: true,
    releaseYear: 2018
  }, {
    id: 'iphone-xr',
    brand: 'Apple',
    model: 'iPhone XR',
    supportsESIM: true,
    releaseYear: 2018
  }, {
    id: 'samsung-s23',
    brand: 'Samsung',
    model: 'Galaxy S23',
    supportsESIM: true,
    releaseYear: 2023
  }, {
    id: 'samsung-s22',
    brand: 'Samsung',
    model: 'Galaxy S22',
    supportsESIM: true,
    releaseYear: 2022
  }, {
    id: 'samsung-s21',
    brand: 'Samsung',
    model: 'Galaxy S21',
    supportsESIM: true,
    releaseYear: 2021
  }, {
    id: 'samsung-s20',
    brand: 'Samsung',
    model: 'Galaxy S20',
    supportsESIM: true,
    releaseYear: 2020
  }, {
    id: 'samsung-note-20',
    brand: 'Samsung',
    model: 'Galaxy Note 20',
    supportsESIM: true,
    releaseYear: 2020
  }, {
    id: 'samsung-fold-4',
    brand: 'Samsung',
    model: 'Galaxy Z Fold 4',
    supportsESIM: true,
    releaseYear: 2022
  }, {
    id: 'google-pixel-7',
    brand: 'Google',
    model: 'Pixel 7',
    supportsESIM: true,
    releaseYear: 2022
  }, {
    id: 'google-pixel-6',
    brand: 'Google',
    model: 'Pixel 6',
    supportsESIM: true,
    releaseYear: 2021
  }, {
    id: 'google-pixel-5',
    brand: 'Google',
    model: 'Pixel 5',
    supportsESIM: true,
    releaseYear: 2020
  }, {
    id: 'google-pixel-4',
    brand: 'Google',
    model: 'Pixel 4',
    supportsESIM: true,
    releaseYear: 2019
  }, {
    id: 'motorola-razr',
    brand: 'Motorola',
    model: 'Razr (2022)',
    supportsESIM: true,
    releaseYear: 2022
  }, {
    id: 'huawei-p40',
    brand: 'Huawei',
    model: 'P40 Pro',
    supportsESIM: true,
    releaseYear: 2020
  }, {
    id: 'oppo-find-x5',
    brand: 'Oppo',
    model: 'Find X5 Pro',
    supportsESIM: true,
    releaseYear: 2022
  }, {
    id: 'xiaomi-12',
    brand: 'Xiaomi',
    model: 'Mi 12',
    supportsESIM: true,
    releaseYear: 2022
  }, {
    id: 'sony-xperia-1-iv',
    brand: 'Sony',
    model: 'Xperia 1 IV',
    supportsESIM: true,
    releaseYear: 2022
  }, {
    id: 'iphone-8',
    brand: 'Apple',
    model: 'iPhone 8',
    supportsESIM: false,
    releaseYear: 2017
  }, {
    id: 'samsung-s10',
    brand: 'Samsung',
    model: 'Galaxy S10',
    supportsESIM: false,
    releaseYear: 2019
  }];
  const filteredDevices = searchQuery ? compatibleDevices.filter(device => `${device.brand} ${device.model}`.toLowerCase().includes(searchQuery.toLowerCase())) : compatibleDevices;
  const faqs = [{
    id: 1,
    question: 'What is an eSIM?',
    answer: "An eSIM (embedded SIM) is a digital SIM that allows you to activate a cellular plan from your carrier without having to use a physical SIM card. It's built into your device and can be programmed to connect to different networks."
  }, {
    id: 2,
    question: 'How do I know if my device supports eSIM?',
    answer: 'You can check our compatibility list above to see if your device is supported. Generally, newer smartphones from major manufacturers (iPhone XS and newer, Samsung Galaxy S20 and newer, Google Pixel 3 and newer) support eSIM technology.'
  }, {
    id: 3,
    question: 'Can I use both physical SIM and eSIM at the same time?',
    answer: 'Yes, most devices that support eSIM allow you to use both a physical SIM and an eSIM simultaneously, enabling dual SIM functionality. This lets you have two different phone numbers or data plans on one device.'
  }, {
    id: 4,
    question: 'What happens if I reset my phone? Will I lose my eSIM?',
    answer: "If you factory reset your device, you will lose your eSIM configuration. You'll need to scan the QR code again or manually enter the details to reactivate your eSIM. We recommend keeping your QR code or activation details safe."
  }, {
    id: 5,
    question: 'Do I need to activate my eSIM before arriving at my destination?',
    answer: 'Yes, we strongly recommend activating your eSIM before arriving at your destination, especially for UAE travel. This ensures you have connectivity as soon as you land. For many countries, including UAE, activation before arrival is required.'
  }, {
    id: 6,
    question: "What if the QR code doesn't work?",
    answer: "If you're having trouble with the QR code, you can manually enter the eSIM details. These are provided in your purchase confirmation email. If you still have issues, please contact our 24/7 support team."
  }];
  return <main className="py-16 w-full bg-[#F9FAFB]" data-id="element-1078">
      <div className="container mx-auto px-4" data-id="element-1079">
        <div className="text-center mb-12" data-id="element-1080">
          <h1 className="text-4xl font-bold mb-4" data-id="element-1081">eSIM Installation Guide</h1>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1082">
            Follow our step-by-step instructions to install and activate your
            TravelSIM eSIM on your device.
          </p>
        </div>
        {/* Device selector tabs */}
        <div className="flex justify-center mb-10" data-id="element-1083">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1" data-id="element-1084">
            <button className={`px-4 py-2 rounded-md font-medium flex items-center ${selectedDevice === 'iphone' ? 'bg-[#2563EB] text-white' : 'text-gray-600'}`} onClick={() => setSelectedDevice('iphone')} data-id="element-1085">
              <AppleIcon className="w-4 h-4 mr-2" data-id="element-1086" />
              iPhone
            </button>
            <button className={`px-4 py-2 rounded-md font-medium flex items-center ${selectedDevice === 'android' ? 'bg-[#2563EB] text-white' : 'text-gray-600'}`} onClick={() => setSelectedDevice('android')} data-id="element-1087">
              <SmartphoneIcon className="w-4 h-4 mr-2" data-id="element-1088" />
              Android
            </button>
          </div>
        </div>
        {/* Installation Steps */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" data-id="element-1089">
          {/* Left column - Phone mockup */}
          <div className="flex justify-center items-center" data-id="element-1090">
            <div className="relative w-[280px] h-[560px]" data-id="element-1091">
              {/* Phone frame */}
              <div className="absolute inset-0 bg-black rounded-[40px] shadow-xl overflow-hidden" data-id="element-1092">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-36 h-6 bg-black rounded-b-xl z-10" data-id="element-1093"></div>
                {/* Screen */}
                <div className="absolute top-0 left-0 right-0 bottom-0 m-2 bg-[#f2f2f7] rounded-[36px] overflow-hidden" data-id="element-1094">
                  {/* Settings screen mockup */}
                  <div className="w-full h-full flex flex-col" data-id="element-1095">
                    {/* Status bar */}
                    <div className="h-10 bg-[#f2f2f7] flex justify-between items-center px-5 pt-2" data-id="element-1096">
                      <span className="text-xs font-semibold" data-id="element-1097">9:41</span>
                      <div className="flex items-center space-x-1" data-id="element-1098">
                        <div className="w-4 h-4 bg-black rounded-full" data-id="element-1099"></div>
                        <div className="w-4 h-4 bg-black rounded-full" data-id="element-1100"></div>
                        <div className="w-4 h-4 bg-black rounded-full" data-id="element-1101"></div>
                      </div>
                    </div>
                    {/* Settings content */}
                    <div className="flex-1 bg-[#f2f2f7] px-4 py-2" data-id="element-1102">
                      <div className="text-center mb-6" data-id="element-1103">
                        <h3 className="text-lg font-semibold" data-id="element-1104">Settings</h3>
                      </div>
                      {/* Settings options */}
                      <div className="bg-white rounded-xl overflow-hidden shadow-sm" data-id="element-1105">
                        <div className="px-4 py-3 border-b border-gray-100 flex items-center" data-id="element-1106">
                          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center mr-3" data-id="element-1107">
                            <SmartphoneIcon className="w-5 h-5 text-white" data-id="element-1108" />
                          </div>
                          <div data-id="element-1109">
                            <p className="font-medium" data-id="element-1110">Mobile Data</p>
                          </div>
                          <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" data-id="element-1111" />
                        </div>
                        <div className="px-4 py-3 border-b border-gray-100 flex items-center bg-blue-50" data-id="element-1112">
                          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center mr-3" data-id="element-1113">
                            <QrCodeIcon className="w-5 h-5 text-white" data-id="element-1114" />
                          </div>
                          <div data-id="element-1115">
                            <p className="font-medium" data-id="element-1116">Add eSIM</p>
                          </div>
                          <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" data-id="element-1117" />
                        </div>
                        <div className="px-4 py-3 flex items-center" data-id="element-1118">
                          <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center mr-3" data-id="element-1119">
                            <WifiIcon className="w-5 h-5 text-white" data-id="element-1120" />
                          </div>
                          <div data-id="element-1121">
                            <p className="font-medium" data-id="element-1122">Wi-Fi</p>
                          </div>
                          <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" data-id="element-1123" />
                        </div>
                      </div>
                      {/* QR scanner overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center" data-id="element-1124">
                        <div className="w-48 h-48 border-2 border-white rounded-lg relative" data-id="element-1125">
                          <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-blue-400" data-id="element-1126"></div>
                          <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-blue-400" data-id="element-1127"></div>
                          <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-blue-400" data-id="element-1128"></div>
                          <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-blue-400" data-id="element-1129"></div>
                        </div>
                        <p className="text-white mt-4 text-sm" data-id="element-1130">Scan QR Code</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Right column - steps */}
          <div data-id="element-1131">
            <h3 className="text-xl font-bold mb-6 text-[#2563EB]" data-id="element-1132">
              {selectedDevice === 'iphone' ? 'iPhone eSIM Installation' : 'Android eSIM Installation'}
            </h3>
            <div className="space-y-6" data-id="element-1133">
              {(selectedDevice === 'iphone' ? iphoneSteps : androidSteps).map(step => <div key={step.id} className="flex bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-1134">
                    <div className="w-10 h-10 rounded-full bg-[#2563EB] text-white flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1135">
                      {step.id}
                    </div>
                    <div className="flex-1" data-id="element-1136">
                      <h4 className="font-semibold text-lg mb-1" data-id="element-1137">
                        {step.title}
                      </h4>
                      <p className="text-gray-600 mb-3" data-id="element-1138">{step.description}</p>
                      <div className="flex items-center text-[#2563EB] text-sm font-medium" data-id="element-1139">
                        {step.icon}
                        <span className="ml-2" data-id="element-1140">
                          {selectedDevice === 'iphone' ? 'Settings > Cellular > Add Cellular Plan' : 'Settings > Network & Internet > SIMs > Add eSIM'}
                        </span>
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>
        </div>
        {/* Important Notes */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg mb-16" data-id="element-1141">
          <div className="flex" data-id="element-1142">
            <div className="flex-shrink-0" data-id="element-1143">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" data-id="element-1144">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" data-id="element-1145" />
              </svg>
            </div>
            <div className="ml-3" data-id="element-1146">
              <h3 className="text-lg font-medium text-yellow-800" data-id="element-1147">
                Important Notes
              </h3>
              <div className="mt-2 text-yellow-700" data-id="element-1148">
                <ul className="list-disc pl-5 space-y-1" data-id="element-1149">
                  <li data-id="element-1150">
                    Activate your eSIM <strong data-id="element-1151">before arriving</strong> at your
                    destination, especially for UAE travel.
                  </li>
                  <li data-id="element-1152">
                    Make sure your device is unlocked and supports eSIM
                    technology.
                  </li>
                  <li data-id="element-1153">
                    You need an active internet connection (Wi-Fi or mobile
                    data) to activate your eSIM.
                  </li>
                  <li data-id="element-1154">
                    Keep your QR code safe - you may need it if you reset your
                    device.
                  </li>
                  <li data-id="element-1155">
                    After activation, you may need to manually select the
                    network in your mobile settings.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        {/* QR Code Example */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-16" data-id="element-1156">
          <div className="text-center mb-8" data-id="element-1157">
            <h2 className="text-2xl font-bold mb-4" data-id="element-1158">Your eSIM QR Code</h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1159">
              After purchasing your eSIM, you'll receive a QR code similar to
              the one below. Use your device's camera to scan this code during
              the installation process.
            </p>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-center gap-8" data-id="element-1160">
            <div className="bg-white p-4 border border-gray-200 rounded-xl" data-id="element-1161">
              <div className="w-48 h-48 bg-[url('https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://example.com/esim-activation')] bg-contain bg-no-repeat bg-center" data-id="element-1162"></div>
            </div>
            <div className="max-w-md" data-id="element-1163">
              <h3 className="font-semibold text-lg mb-3" data-id="element-1164">
                Alternative Activation Method
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-1165">
                If you're unable to scan the QR code, you can manually enter the
                activation details:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm" data-id="element-1166">
                <p className="mb-2" data-id="element-1167">SM-DP+ Address: rsp.travelsim.com</p>
                <p className="mb-2" data-id="element-1168">
                  Activation Code: LPA:1$rsp.travelsim.com$ABC123-DEF456-GHI789
                </p>
                <p data-id="element-1169">Confirmation Code: 1234-5678</p>
              </div>
            </div>
          </div>
        </div>
        {/* Device Compatibility */}
        <div className="mb-16" data-id="element-1170">
          <div className="text-center mb-8" data-id="element-1171">
            <h2 className="text-2xl font-bold mb-4" data-id="element-1172">Device Compatibility</h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1173">
              Check if your device supports eSIM technology. We've compiled a
              list of compatible devices from major manufacturers.
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8" data-id="element-1174">
            <div className="relative mb-6" data-id="element-1175">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1176">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1177" />
              </div>
              <input type="text" placeholder="Search for your device (e.g., iPhone 13, Galaxy S22)..." className="pl-10 pr-4 py-3 border border-gray-300 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1178" />
            </div>
            <div className="overflow-x-auto" data-id="element-1179">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1180">
                <thead className="bg-gray-50" data-id="element-1181">
                  <tr data-id="element-1182">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1183">
                      Brand
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1184">
                      Model
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1185">
                      Release Year
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1186">
                      eSIM Support
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1187">
                  {filteredDevices.map(device => <tr key={device.id} data-id="element-1188">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1189">
                        {device.brand}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1190">
                        {device.model}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1191">
                        {device.releaseYear}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1192">
                        {device.supportsESIM ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1193">
                            <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-1194" />{' '}
                            Supported
                          </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1195">
                            <XCircleIcon className="h-4 w-4 mr-1" data-id="element-1196" /> Not
                            Supported
                          </span>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {filteredDevices.length === 0 && <div className="text-center py-8" data-id="element-1197">
                <p className="text-gray-500" data-id="element-1198">
                  No devices found matching your search criteria.
                </p>
              </div>}
          </div>
          <div className="text-center" data-id="element-1199">
            <p className="text-gray-600 mb-4" data-id="element-1200">
              Don't see your device? Contact our support team for assistance.
            </p>
            <Button variant="outline" data-id="element-1201">Contact Support</Button>
          </div>
        </div>
        {/* FAQ Section */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-16" data-id="element-1202">
          <h2 className="text-2xl font-bold mb-6" data-id="element-1203">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4" data-id="element-1204">
            {faqs.map(faq => <div key={faq.id} className="border border-gray-200 rounded-lg" data-id="element-1205">
                <button className="flex justify-between items-center w-full px-6 py-4 text-left focus:outline-none" onClick={() => toggleFaq(faq.id)} data-id="element-1206">
                  <h3 className="font-semibold text-lg" data-id="element-1207">{faq.question}</h3>
                  {expandedFaq === faq.id ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-1208" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-1209" />}
                </button>
                {expandedFaq === faq.id && <div className="px-6 py-4 border-t border-gray-200" data-id="element-1210">
                    <p className="text-gray-600" data-id="element-1211">{faq.answer}</p>
                  </div>}
              </div>)}
          </div>
        </div>
        {/* Support Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-8 text-white" data-id="element-1212">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1213">
            <h2 className="text-2xl font-bold mb-4" data-id="element-1214">Need Additional Help?</h2>
            <p className="text-blue-100 mb-6" data-id="element-1215">
              Our support team is available 24/7 to assist you with any
              questions or issues you may have with your eSIM installation.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1216">
              <Button variant="primary" className="bg-white text-blue-600 hover:bg-gray-100" data-id="element-1217">
                Contact Support
              </Button>
              <Button variant="outline" className="border-white text-white hover:bg-blue-700" data-id="element-1218">
                Live Chat
              </Button>
            </div>
          </div>
        </div>
      </div>
    </main>;
}